import React, { useState, useEffect } from 'react';
import { useUser } from '../userContext';

//const IP = "http://localhost:8000/chat"; // local
const IP = "/api/chat"; // production
const DB_API = "https://chatbot.aux-rolplay.com/admin-api"; // DB API for FAQs

const Chat = () => {
  const { clientName, userId } = useUser();

  // Clientes para los que se debe ocultar el botón de ticket (WhatsApp).
  // Agregar más nombres en minúsculas aquí cuando sea necesario.
  const HIDDEN_TICKET_CLIENTS = new Set(['perfil-gentera']);
  const normalizedClient = clientName ? clientName.toLowerCase().trim() : '';

  // Configuration variable to enable/disable attachment feature
  const ATTACHMENT_ENABLED = 0; // Change to 1 to enable attachment functionality
  // Configuration variable to enable/disable ticket feature
  const TICKET_ENABLED = 1; // Change to 0 to disable ticket functionality
  const TICKET_URL = "https://wa.me/5218119655208"; // URL for ticket creation

  const [messages, setMessages] = useState([
    { id: 1, text: "Hola! Soy tu asistente de Soporte Técnico RolPlay. Escribe tu duda a continuación y te ayudaré con tu problema o si deseas contactar con un agente en vivo, puedes hacer clic en el icono de WhatsApp que se encuentra en la esquina superior derecha de esta ventana.", sender: "bot" }
  ]);
  const [inputText, setInputText] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [showFaqDropdown, setShowFaqDropdown] = useState(false);

  // Load FAQs from DB API when component mounts or client changes
  useEffect(() => {
    if (!clientName) return;
    const loadFaqs = async () => {
      try {
        const response = await fetch(`${DB_API}/clients/${clientName}`);
        if (!response.ok) {
          console.warn(`No client profile found for: ${clientName}`);
          setFaqs([]);
          return;
        }
        const data = await response.json();
        // DB stores faqs as { "faqs": [...] } so we access data.faqs.faqs
        const raw = data.faqs;
        if (Array.isArray(raw)) {
          setFaqs(raw);                          // stored directly as array
        } else if (raw && Array.isArray(raw.faqs)) {
          setFaqs(raw.faqs);                     // stored as { faqs: [...] }
        } else {
          setFaqs([]);
        }
      } catch (error) {
        console.error('Error loading FAQs from DB API:', error);
        setFaqs([]);
      }
    };
    loadFaqs();
  }, [clientName]);


  const handleSendMessage = async () => {
    if (inputText.trim() && !isLoading) {
      const newMessage = {
        id: Date.now(),
        text: inputText,
        sender: "user",
        timestamp: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
      };

      setMessages(prev => [...prev, newMessage]);
      const messageToSend = inputText;
      setInputText('');
      setIsLoading(true);

      try {
        const response = await fetch(IP, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message: messageToSend,
            client: clientName,
            user_id: userId
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        const botResponse = {
          id: Date.now() + 1,
          text: data.response,
          sender: "bot",
          timestamp: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
          problema: data.problema,
          confidence: data.confidence
        };

        setMessages(prev => [...prev, botResponse]);
      } catch (error) {
        console.error('Error sending message:', error);
        const errorResponse = {
          id: Date.now() + 1,
          text: "Lo siento, hubo un error al procesar tu mensaje. Por favor, intenta nuevamente.",
          sender: "bot",
          timestamp: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
        };
        setMessages(prev => [...prev, errorResponse]);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleFaqClick = async (faq) => {
    const faqMessage = {
      id: Date.now(),
      text: faq,
      sender: "user",
      timestamp: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
    };
    setMessages(prev => [...prev, faqMessage]);
    setIsLoading(true);

    try {
      const response = await fetch(IP, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: faq,
          client: clientName,
          user_id: userId
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      const botResponse = {
        id: Date.now() + 1,
        text: data.response,
        sender: "bot",
        timestamp: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
        problema: data.problema,
        confidence: data.confidence
      };

      setMessages(prev => [...prev, botResponse]);
    } catch (error) {
      console.error('Error sending FAQ:', error);
      const errorResponse = {
        id: Date.now() + 1,
        text: "Lo siento, hubo un error al procesar tu consulta. Por favor, intenta nuevamente.",
        sender: "bot",
        timestamp: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
      };
      setMessages(prev => [...prev, errorResponse]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAttachmentClick = () => {
    if (!ATTACHMENT_ENABLED) {
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 2000);
      return;
    }
    // Future file attachment logic here
    console.log('File attachment clicked');
  };

  const handleTicketClick = () => {
    if (TICKET_ENABLED) {
      window.open(TICKET_URL, '_blank');
    }
  };

  const handleFaqDropdownToggle = () => {
    setShowFaqDropdown(!showFaqDropdown);
  };

  const handleFaqClickFromDropdown = (faq) => {
    setShowFaqDropdown(false);
    handleFaqClick(faq);
  };

  return (
    <div className="w-full h-full bg-white rounded-3xl shadow-red-100/40 shadow-xl flex flex-col overflow-hidden font-inter">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-500 to-red-600 text-white px-8 py-4 flex items-center justify-between rounded-t-3xl relative">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2v6m0 8v6m10-10h-6M8 12H2" />
              </svg>
            </div>
            <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-green-400 rounded-full border-2 border-white shadow-sm"></div>
          </div>
          <div>
            <h3 className="font-bold text-sm tracking-tight">Soporte Técnico</h3>
            <p className="text-white/70 text-xs font-medium">En línea • Respuesta rápida</p>
          </div>
        </div>
        <div className="flex items-center space-x-2 relative">
          {/* FAQ Button */}
          {faqs.length > 0 && (
            <div className="relative">
              <button
                onClick={handleFaqDropdownToggle}
                className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all duration-200 backdrop-blur-sm"
                title="FAQs"
              >
                <svg className="w-4 h-4 text-white/80 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>

              {/* FAQ Dropdown */}
              {showFaqDropdown && (
                <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 z-50 animate-fadeIn">
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-gray-800 mb-3">Preguntas frecuentes</h3>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {faqs.map((faq, index) => (
                        <button
                          key={index}
                          onClick={() => handleFaqClickFromDropdown(faq)}
                          className="w-full text-left bg-red-50 hover:bg-red-100 text-red-600 text-xs px-3 py-2 rounded-lg transition-all duration-200 font-medium"
                        >
                          {faq}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Ticket Button */}
          {TICKET_ENABLED && !HIDDEN_TICKET_CLIENTS.has(normalizedClient) && (
            <button
              onClick={handleTicketClick}
              className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all duration-200 backdrop-blur-sm animate-blink"
              title="Crear ticket"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-brand-whatsapp"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M3 21l1.65 -3.8a9 9 0 1 1 3.4 2.9l-5.05 .9" /><path d="M9 10a.5 .5 0 0 0 1 0v-1a.5 .5 0 0 0 -1 0v1a5 5 0 0 0 5 5h1a.5 .5 0 0 0 0 -1h-1a.5 .5 0 0 0 0 1" /></svg>
            </button>
          )}
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 px-7 py-6 overflow-y-auto bg-gray-25 space-y-4">
        {messages.map((message) => (
          <div key={message.id} className={`flex flex-col ${message.sender === 'user' ? 'items-end' : 'items-start'}`}>
            <div className={`flex items-center space-x-2 mb-1 ${message.sender === 'user' ? 'flex-row-reverse space-x-reverse' : 'flex-row'}`}>
              <p className="text-xs font-semibold text-gray-600">
                {message.sender === 'user' ? 'Usuario' : 'Soporte Técnico'}
              </p>
              <p className="text-xs text-gray-400">
                {message.timestamp || new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
            <div className={`max-w-sm px-5 py-4 rounded-2xl ${message.sender === 'user'
                ? 'bg-gradient-to-br from-red-500 to-red-600 text-white shadow-red-200/30 shadow-md'
                : 'bg-gray-100 text-gray-800 shadow-gray-200/40 shadow-sm'
              }`}>
              <p className="text-xs leading-relaxed font-medium">{message.text}</p>
              {message.sender === 'bot' && message.confidence && (
                <p className="text-xs text-gray-500 mt-1 opacity-60">
                  Confianza: {Math.round(message.confidence * 100)}%
                </p>
              )}
            </div>
          </div>
        ))}

        {/* Loading indicator */}
        {isLoading && (
          <div className="flex items-start">
            <div className="bg-gray-100 text-gray-800 shadow-gray-200/40 shadow-sm max-w-sm px-5 py-4 rounded-2xl">
              <div className="flex items-center space-x-2">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
                <p className="text-xs text-gray-500">Escribiendo...</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="bg-white px-7 py-3 rounded-b-3xl">
        <div className="flex space-x-3">
          {/* Attachment Button */}
          <div className="relative">
            <button
              onClick={handleAttachmentClick}
              className={`rounded-full p-3 transition-all duration-200 ${ATTACHMENT_ENABLED
                  ? 'bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-700'
                  : 'bg-gray-50 text-gray-300 cursor-not-allowed opacity-50'
                }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
            </button>

            {/* Tooltip */}
            {showTooltip && (
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-800 text-white text-xs rounded-lg whitespace-nowrap animate-fadeIn">
                No disponible
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
              </div>
            )}
          </div>

          <input
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !isLoading && handleSendMessage()}
            placeholder="Escribe tu mensaje..."
            disabled={isLoading}
            className="flex-1 bg-gray-100 focus:bg-white rounded-full px-6 py-3 text-xs focus:outline-none focus:ring-2 focus:ring-red-200 placeholder-gray-500 shadow-gray-200/30 shadow-sm font-medium transition-colors duration-200 disabled:opacity-50"
          />

          {/* Send Button */}
          <button
            onClick={handleSendMessage}
            disabled={isLoading}
            className="bg-gradient-to-br from-red-500 to-red-600 text-white rounded-full p-3 hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-red-200/40 shadow-md hover:shadow-red-300/50 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
      </div>

      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        
        .font-inter {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.9) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        
        @keyframes blink {
          0%, 100% { 
            opacity: 1; 
            box-shadow: 0 0 0 rgba(255, 255, 255, 0);
          }
          50% { 
            opacity: 0.2; 
            box-shadow: 0 0 8px rgba(255, 255, 255, 0.6);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        
        .animate-blink {
          animation: blink 2.5s ease-in-out infinite;
        }
        
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .bg-gray-25 {
          background-color: #ffffff;
        }
      `}</style>
    </div>
  );
};

export default Chat;